import { api } from '@cwd/lib/main';
import { createListWithDetails, addContactsToList } from '@views/Lists/Lists.access';
import { formatDate } from '@shared/utils/formatDateTime';

export enum UserAccountRole {
    Administrator = 0,
    User = 1,
    Custom = 2,
}

export interface AccountUser {
    UserId: string;
    Name: string;
    Email: string;
    Created: string;
    LastLogin: string | null;
    ActiveTask: number;
    ActiveApiKeyCount: number;
    UserAccountRole: UserAccountRole | null;
    CustomRoleId: string | null;
    HasTotpKey: boolean;
}

export interface RoleSummary {
    RoleId: string;
    Name: string;
    InternalDescription: string | null;
}

export enum AccountUserInviteStatus {
    Pending = 0,
    PendingAlreadyUser = 1,
    Accepted = 2,
    Revoked = 3,
    Archived = 4,
}

export interface AccountUserInvite {
    AccountUserInviteId: string;
    Email: string;
    FirstName: string | null;
    LastName: string | null;
    ExpireDate: string;
    Status: AccountUserInviteStatus;
    RoleType: UserAccountRole;
    RoleName: string | null;
}

export interface AcceptUserInvite {
    FirstName: string | null;
    LastName: string | null;
    Email: string;
    Password: string;
    DeviceId: string;
}

export async function getUsers(): Promise<AccountUser[]> {
    return api.get('/Users?$orderby=Email');
}

export async function exportUsersToExcel(): Promise<{ Url: string }> {
    return api.get('/Users/Export?$orderby=Email&$format=excel');
}

export async function removeUser(userId: string): Promise<void> {
    return api.post(`/Users/${userId}/RemoveUser/`);
}

export async function resetTotpKey(userId: string): Promise<void> {
    return api.post(`/Users/${userId}/ResetTotpKey/`);
}

export async function getRoles(): Promise<RoleSummary[]> {
    return api.get('/Roles');
}

export async function setUserRole(userId: string, roleType: UserAccountRole, customRoleId: string | null): Promise<void> {
    return api.post(`/Roles/SetRole/${userId}`, { roletyp: roleType, customRoleId });
}

export async function changeOwnerOfTasks(fromUserId: string, toUserId: string): Promise<void> {
    return api.post(`/Accounts/ChangeOwnerOfTasksPerAccount?fromUserId=${fromUserId}&toUserId=${toUserId}`, null);
}

export async function changeOwnerOfApiKeys(fromUserId: string, toUserId: string): Promise<void> {
    return api.post(`/Accounts/ChangeOwnerOfApiKeysPerAccount?fromUserId=${fromUserId}&toUserId=${toUserId}`, null);
}

export async function inviteUser(email: string, roleType: UserAccountRole, roleId: string | null): Promise<AccountUserInvite> {
    return api.post('/Users/Invites', { email, roleType, roleId });
}

export async function getPendingInvites(): Promise<AccountUserInvite[]> {
    return api.get('/Users/Invites/Pending');
}

export async function revokeInvite(inviteId: string): Promise<AccountUserInvite> {
    return api.post(`/Users/Invites/${inviteId}/Revoke`);
}

export async function acceptInvite(inviteId: string, user: AcceptUserInvite): Promise<{ token: string | null }> {
    return api.post(`/Users/Invites/${inviteId}/Accept`, user);
}

interface UserContactLink {
    UserId: string;
    ContactId: string;
}

// Finds-or-creates each user's Contact by email and links it on UserAccount.UserContactId (once,
// never overwritten on later exports), so the contact a user maps to stays traceable.
export async function linkUsersToContacts(userIds: string[]): Promise<UserContactLink[]> {
    return api.post('/Users/LinkContacts', userIds);
}

async function addUsersAsContacts(users: AccountUser[], listId: string): Promise<void> {
    const links = await linkUsersToContacts(users.map((u) => u.UserId));
    await addContactsToList(
        listId,
        links.map((l) => l.ContactId)
    );
}

export async function exportUsersToNewList(users: AccountUser[]): Promise<{ ListId: string }> {
    const list = await createListWithDetails(
        `${$Resources.AccountUsersExportToListName} ${formatDate(new Date())}`,
        $Resources.AccountUsersExportToListDescription
    );
    await addUsersAsContacts(users, list.ListId);
    return { ListId: list.ListId };
}

export async function exportUsersToExistingList(users: AccountUser[], targetListId: string): Promise<{ ListId: string }> {
    await addUsersAsContacts(users, targetListId);
    return { ListId: targetListId };
}
